import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { openPromptStringFieldDialog } from '@martinus/styles/client/dialogs/PromptStringFieldDialog.js';
import { html, nothing } from 'lit';
import { filterDummyEmail } from '../../../db/utils/util-csv.js';
import { openChangeDebitorennummerDialog } from '../../../debitor-numbers/client/ChangeDebitornummerDialog.js';
import { buildLegacyDebitorNumberLabels } from '../../../debitor-numbers/legacyDebitorNumberLabels.js';
import { getAllSchools } from '../../../groups/api/getAllSchools.js';
import { hasRoles } from '../../../permissions/api/hasRoles.js';
import { useMartinusABAC } from '../../../permissions/client/useMartinusAbac.js';
import { EMPLOYEE_ROLES } from '../../../permissions/roles.js';
import { showChangeEmailProcessDialog } from '../../../user-emails/change-email-address/sg-dialog-change-email-process.js';
import { startEmailVerificationProcess } from '../../../user-emails/client/startEmailVerificationProcess.js';
import type { Person } from '../../entities/person.js';
import type { UserOnUserPayload } from '../../UserPolicy.js';
import { UserProfileActions } from '../UserProfilePolicy.js';

export const UserInternalIdentifiers = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  const user = useObservable(() => currentUser<Person>(), []);
  if (!user) return nothing;

  const { can: canSendVerificationMail } = useMartinusABAC<UserOnUserPayload>({
    action: UserProfileActions.SEND_VERIFICATION_MAIL,
    viewedUser: props.form.document,
  });

  const { can: canEditMail } = useMartinusABAC<UserOnUserPayload>({
    action: UserProfileActions.EDIT_EMAIL,
    viewedUser: props.form.document,
  });

  const { can: canEditUsername } = useMartinusABAC<UserOnUserPayload>({
    action: UserProfileActions.EDIT_USERNAME,
    viewedUser: props.form.document,
  });

  const { can: canEditDebitorennummer } = useMartinusABAC<UserOnUserPayload>({
    action: UserProfileActions.EDIT_DEBITORENNUMMER,
    viewedUser: props.form.document,
  });

  const { can: canEditKuerzel } = useMartinusABAC<UserOnUserPayload>({
    action: UserProfileActions.EDIT_KUERZEL,
    viewedUser: props.form.document,
  });

  // the scope of a legacy debitor number is a school, so we resolve it against the school cache to show its kuerzel
  const { value: schools } = useQuery(() => getAllSchools(), []);
  const legacyDebitorNumberLabels = buildLegacyDebitorNumberLabels(props.form.document.legacyDebitorNumbers, schools);

  const emailConfirmed = props.form.document.emailConfirmed;

  const personIsEmployee = hasRoles({
    user: props.form.document,
    roles: EMPLOYEE_ROLES,
  });

  return html`
    <div class="card">
      <div class="card-body flex-row flex-wrap">
        <div class="flex flex-row items-center gap-2">
          <p class="text-sm font-semibold text-base-content/70">E-Mail-Adresse:</p>
          <div
            ${hoverTooltip()}
            class="badge badge-lg ${
              emailConfirmed
                ? 'badge-success badge-outline '
                : `badge-warning badge-dash ${canSendVerificationMail ? 'cursor-pointer tooltip' : ''}`
            }"
            @click=${async () => {
              if (emailConfirmed || !canSendVerificationMail) return;

              if (canEditMail && user._id !== props.form.document._id) {
                const verifyDirectly = await confirmDialog('Möchten Sie die E-Mail-Adresse direkt verifizieren?', {
                  highlightDecline: true,
                  acceptText: 'Ja, direkt verifizieren.',
                  declineText: 'Nein, nur Verifizierungsmail versenden.',
                });
                if (verifyDirectly) {
                  await guardDialog(
                    async () => {
                      props.form.document.emailConfirmed = true;
                      const constructed = constructValue(props.form.document);
                      await constructed.save();
                    },
                    {
                      successText: 'Die E-Mail-Adresse wurde direkt verifiziert.',
                    },
                  );
                  return;
                }
              }

              await startEmailVerificationProcess(user, props.form.document);
            }}
          >
            ${
              !emailConfirmed && canSendVerificationMail
                ? html`
                    <div class="tooltip-content">
                      Ihre E-Mail-Adresse ist noch nicht verifiziert. Klicken Sie hier um eine Verifizierungsmail zu
                      versenden.
                    </div>
                  `
                : nothing
            }
            <span>${filterDummyEmail(props.form.document.email)}</span>

            ${
              canEditMail
                ? html`
                    <button
                      class="btn btn-ghost btn-circle btn-dash btn-xs -mr-[13px]"
                      @click=${async (e: MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        await showChangeEmailProcessDialog({
                          user: props.form.document,
                          userIsCurrentUser: props.form.document._id === user._id,
                        });
                      }}
                    >
                      <span class="icon-[material-symbols--edit] text-lg"></span>
                    </button>
                  `
                : nothing
            }
          </div>
        </div>

        <div class="flex flex-row items-center gap-2 w-fit">
          <p class="text-sm font-semibold text-base-content/70">Nutzername</p>
          <div class="badge badge-lg badge-outline">
            <p>${props.form.document.username}</p>

            ${
              canEditUsername
                ? html`
                    <button
                      class="btn btn-ghost btn-circle btn-dash btn-xs -mr-[13px]"
                      @click=${async () => {
                        const { value } = await openPromptStringFieldDialog(props.form.document.username, {
                          title: 'Nutzernamen ändern',
                          placeholder: 'Nutzername',
                          description:
                            'Hier können Sie den Nutzernamen für den Benutzer verwalten. Nutzer können sich zusätzlich zu ihrer E-Mail-Adresse auch über ihren Nutzernamen anmelden um eventuelle Abkürzungen zu verwenden.',
                        });
                        if (value) {
                          props.form.document.username = value;
                        }
                      }}
                    >
                      <span class="icon-[material-symbols--edit] text-lg"></span>
                    </button>
                  `
                : nothing
            }
          </div>
        </div>

        <div class="flex flex-row items-center gap-2 w-fit">
          <p class="text-sm font-semibold text-base-content/70">Debitorennummer</p>
          <div class="badge badge-lg badge-outline">
            <p class="text-primary font-bold">${props.form.document.debitorennummer}</p>
            ${
              legacyDebitorNumberLabels.length
                ? html`<p class="text-base-content/70">(alt: ${legacyDebitorNumberLabels.join(', ')})</p>`
                : nothing
            }
            ${
              canEditDebitorennummer
                ? html`
                    <button
                      class="btn btn-ghost btn-circle btn-dash btn-xs -mr-[13px]"
                      @click=${async () => {
                        const { debitorennummer, legacyDebitorNumbers } = await openChangeDebitorennummerDialog(
                          props.form.document.debitorennummer,
                          props.form.document.legacyDebitorNumbers,
                        );

                        props.form.document.debitorennummer = debitorennummer;
                        props.form.document.legacyDebitorNumbers = legacyDebitorNumbers;
                      }}
                    >
                      <span class="icon-[material-symbols--edit] text-lg"></span>
                    </button>
                  `
                : nothing
            }
          </div>
        </div>

        ${
          personIsEmployee
            ? html`
                <div class="flex flex-row items-center gap-2 w-fit">
                  <p class="text-sm font-semibold text-base-content/70">Lehrerkürzel</p>
                  <div class="badge badge-lg badge-outline">
                    <p>${props.form.document.nameAbbreviation ?? '---'}</p>

                    ${
                      canEditKuerzel
                        ? html`
                            <button
                              class="btn btn-ghost btn-circle btn-dash btn-xs -mr-[13px]"
                              @click=${async () => {
                                const { value } = await openPromptStringFieldDialog(
                                  props.form.document.nameAbbreviation,
                                  {
                                    title: 'Lehrerkürzel ändern',
                                    placeholder: 'Lehrerkürzel',
                                    description:
                                      'Hier können Sie das Lehrerkürzel für den Benutzer verwalten. Diese Kürzel wird in den Fremdsystemen, wie UNTIS oder edoo.sys geführt.',
                                  },
                                );
                                if (value) {
                                  props.form.document.nameAbbreviation = value;
                                }
                              }}
                            >
                              <span class="icon-[material-symbols--edit] text-lg"></span>
                            </button>
                          `
                        : nothing
                    }
                  </div>
                </div>
              `
            : nothing
        }
      </div>
    </div>
  `;
});
